import { useAuth0 } from "@auth0/auth0-react";
import { AxiosError } from "axios";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import ConversationDomain from "entities/domain/conversations/conversation-domain";
import MessageDomain from "entities/domain/conversations/message-domain";
import ReplySuggestionDomain from "entities/domain/reply_suggestion";
import TemplateDomain from "entities/domain/templates";
import {
  ConversationsListParams,
  conversationMessagesQueryKey,
  conversationQueryKey,
  conversationsListQueryKey,
  fetchConversationsListPage,
} from "hooks/queries/conversations-list";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useInboxScope } from "hooks/use-inbox-scope";
import { useMemo } from "react";
import {
  ConversationTab,
  activeTabSelector,
  filterAgentsSelector,
  filterChannelsSelector,
  filterCustomerTagIdsSelector,
  filterSentimentSelector,
  filterUrgencySelector,
  openClosedSelector,
  searchFieldSelector,
  searchTextSelector,
  sortOrderSelector,
} from "redux/features/conversations";
import { useAppSelector } from "redux/hooks";
import InboxService from "services/inbox";
import ReplySuggestionService from "services/replySuggestion";
import TemplatesService from "services/templates";
import {
  numberOfConversationsPerLoad,
  numberOfMessagesPerLoad,
} from "util/constants";
import {
  ALL_INBOXES_SCOPE,
  InboxScope,
  serializeInboxScope,
} from "util/inbox-scope";

export {
  conversationMessagesQueryKey,
  conversationQueryKey,
  conversationsListQueryKey,
  fetchConversationsListPage,
};
export type { ConversationsListParams };

/** Every conversation templates entry of a merchant, whatever the scope. */
export const conversationTemplatesMerchantQueryKey = (
  merchantId: number | null | undefined
) => ["conversations", "templates", merchantId ?? null];

/** Every conversation templates entry of one conversation, scoped or not. */
export const conversationTemplatesConversationQueryKey = (
  merchantId: number | null | undefined,
  conversationId: number | null | undefined
) => [
  ...conversationTemplatesMerchantQueryKey(merchantId),
  conversationId ?? null,
];

export const conversationTemplatesQueryKey = (
  merchantId: number | null | undefined,
  conversationId: number | null | undefined,
  agentId: number | null | undefined,
  inboxScope: InboxScope | null
) => [
  ...conversationTemplatesConversationQueryKey(merchantId, conversationId),
  agentId ?? null,
  // One entry per Inbox, and one for the unfiltered request ("all"): the
  // picker's "All templates" is a different question, not a filter over an
  // Inbox's response. An unresolved scope (null) never fetches, and its own
  // key keeps it from reading the unfiltered entry meanwhile.
  inboxScope ? serializeInboxScope(inboxScope) : null,
];

export const replySuggestionQueryKey = (
  merchantId: number | null | undefined,
  conversationId: number | null | undefined
) => [
  "conversations",
  "reply-suggestion",
  merchantId ?? null,
  conversationId ?? null,
];

export const aiBoundaryQueryKey = (
  merchantId: number | null | undefined,
  conversationId: number | null | undefined
) => [
  "conversations",
  "ai-boundary",
  merchantId ?? null,
  conversationId ?? null,
];

export const conversationByChannelIdQueryKey = (
  merchantId: number | null | undefined,
  channelId: string | null | undefined
) => ["conversations", "by-channel-id", merchantId ?? null, channelId ?? null];

export function useConversationsInfinite(
  params: Omit<ConversationsListParams, "merchantId" | "offset">,
  options?: { staleTimeMs?: number; enabled?: boolean }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const { data: currentAgent } = useCurrentAgent();

  return useInfiniteQuery<ConversationDomain[], Error>({
    queryKey: conversationsListQueryKey(merchant?.id, params),
    queryFn: async ({ pageParam = 0, signal }) =>
      fetchConversationsListPage(
        auth0Context,
        merchant?.id,
        params,
        pageParam as number,
        signal
      ),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < numberOfConversationsPerLoad) {
        return undefined;
      }
      return allPages.length * numberOfConversationsPerLoad;
    },
    initialPageParam: 0,
    enabled:
      Boolean(merchant?.id && currentAgent?.id) && (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 60_000,
  });
}

/**
 * The conversations list the inbox is showing: one query with every filter,
 * the Inbox scope and the sort order. The list and the mobile bulk actions
 * both read it, so a selection made in the list resolves against the same
 * loaded pages. A second query keyed differently — without the sort order,
 * say — holds other conversations, and a selection would not resolve in it.
 */
export function useVisibleConversationsList() {
  const { data: merchant } = useMerchant();
  const { data: currentAgent } = useCurrentAgent();
  const activeTab = useAppSelector(activeTabSelector);
  const isOpenOrClosed = useAppSelector(openClosedSelector);
  const searchText = useAppSelector(searchTextSelector);
  const searchField = useAppSelector(searchFieldSelector);
  const filterChannels = useAppSelector(filterChannelsSelector);
  const filterAgents = useAppSelector(filterAgentsSelector);
  const filterCustomerTagIds = useAppSelector(filterCustomerTagIdsSelector);
  const filterSentiment = useAppSelector(filterSentimentSelector);
  const filterUrgency = useAppSelector(filterUrgencySelector);
  const sortOrder = useAppSelector(sortOrderSelector);
  // URL first, shared with the prefetch and the templates query. `null` while
  // a Release 1 link waits for the Inbox list.
  const inboxScope = useInboxScope();

  return useConversationsInfinite(
    {
      tab: activeTab || ConversationTab.Team,
      status: isOpenOrClosed,
      searchText,
      searchField,
      channels: filterChannels,
      agents: filterAgents,
      tagIds: filterCustomerTagIds,
      inboxScope: inboxScope ?? ALL_INBOXES_SCOPE,
      sentiment: filterSentiment,
      urgency: filterUrgency,
      sortOrder,
    },
    {
      enabled:
        !!merchant?.id && !!currentAgent?.id && !!activeTab && !!inboxScope,
    }
  );
}

export function useConversation(
  conversationId: number | undefined,
  options?: { staleTimeMs?: number; enabled?: boolean }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<ConversationDomain | null, Error>({
    queryKey: conversationQueryKey(merchant?.id, conversationId),
    queryFn: async ({ signal }) => {
      if (!merchant?.id || !conversationId) return null;

      try {
        return await InboxService.getConversation(
          auth0Context,
          conversationId,
          merchant.id,
          signal
        );
      } catch (error) {
        // null means "there is no such conversation", and the inbox navigates
        // out of the thread on it. Only a 404 may say that: swallowing an
        // aborted fetch (react-query cancels in-flight requests whenever the
        // key changes or the observer unmounts) or a transient failure would
        // bounce an agent out of a conversation that exists.
        if (error instanceof AxiosError && error.response?.status === 404) {
          return null;
        }

        throw error;
      }
    },
    enabled:
      Boolean(merchant?.id && conversationId) && (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 60_000,
  });
}

export function useConversationMessagesInfinite(
  conversationId: number | undefined,
  options?: { staleTimeMs?: number; enabled?: boolean }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useInfiniteQuery<MessageDomain[], Error>({
    queryKey: conversationMessagesQueryKey(merchant?.id, conversationId),
    queryFn: async ({ pageParam = 0, signal }) => {
      if (!merchant?.id || !conversationId) return [];

      return InboxService.getMessagesInConversation(
        auth0Context,
        merchant.id,
        conversationId,
        pageParam as number,
        signal
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < numberOfMessagesPerLoad) {
        return undefined;
      }
      return allPages.length * numberOfMessagesPerLoad;
    },
    initialPageParam: 0,
    enabled:
      Boolean(merchant?.id && conversationId) && (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 60_000,
  });
}

export function useConversationTemplates(
  conversationId: number | undefined,
  options?: {
    staleTimeMs?: number;
    enabled?: boolean;
    /**
     * Overrides the inbox's scope for this one query — how the picker's
     * "All templates" option drops the Inbox filter. Omit it everywhere else.
     */
    inboxScope?: InboxScope;
  }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const { data: currentAgent } = useCurrentAgent();
  // Resolved here rather than passed in: every caller wants the templates the
  // current inbox is scoped to, and the earliest fetch is a warm-up in
  // components/chat, which would otherwise fill an unscoped cache entry.
  // `null` while a Release 1 link waits for the Inbox list: nothing is asked
  // for until it names an Inbox.
  const currentInboxScope = useInboxScope();
  const inboxScope = options?.inboxScope ?? currentInboxScope;

  return useQuery<TemplateDomain[], Error>({
    queryKey: conversationTemplatesQueryKey(
      merchant?.id,
      conversationId,
      currentAgent?.id ?? null,
      inboxScope
    ),
    queryFn: async ({ signal }) => {
      if (
        !merchant?.id ||
        !conversationId ||
        !currentAgent?.id ||
        inboxScope === null
      ) {
        return [];
      }

      return TemplatesService.getTemplatesForConversation(
        auth0Context,
        conversationId,
        merchant,
        currentAgent,
        inboxScope,
        signal
      );
    },
    enabled:
      Boolean(merchant?.id && conversationId && currentAgent?.id) &&
      inboxScope !== null &&
      (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 60_000,
  });
}

const findTemplateById = (
  templates: TemplateDomain[] | undefined,
  templateId: string | null | undefined
): TemplateDomain | null =>
  templates?.find((template) => template.id === templateId) ?? null;

export function useSelectedConversationTemplate(
  conversationId: number | undefined,
  templateId: string | null | undefined
) {
  const { data: scopedTemplates } = useConversationTemplates(conversationId, {
    enabled: Boolean(conversationId && templateId),
  });
  // A template picked through "All templates" is only in the unfiltered
  // response, which the picker has usually cached already. Read only when
  // the Inbox's own response lacks it; under All it is the same entry.
  const isMissingFromScope =
    scopedTemplates !== undefined &&
    findTemplateById(scopedTemplates, templateId) === null;
  const { data: unfilteredTemplates } = useConversationTemplates(
    conversationId,
    {
      enabled: Boolean(conversationId && templateId) && isMissingFromScope,
      inboxScope: ALL_INBOXES_SCOPE,
    }
  );
  const templates = isMissingFromScope ? unfilteredTemplates : scopedTemplates;

  const selectedTemplateId = useAppSelector(
    (state) => state.conversations.messageInput.templateId
  );
  const draftCustomFields = useAppSelector(
    (state) => state.conversations.messageInput.customFields
  );

  return useMemo(() => {
    if (!templates || !templateId) return null;
    const base = findTemplateById(templates, templateId);
    if (!base) return null;

    // When the template is selected in the message input, overlay draft custom field
    // values from Redux so preview + validations reflect what the user has typed.
    if (selectedTemplateId !== templateId) {
      return base;
    }

    const mergedCustomFields = {
      ...(base.customFields || {}),
      ...(draftCustomFields || {}),
    };

    return new TemplateDomain(
      base.id,
      base.name,
      base.title,
      base.text,
      base.purpose,
      mergedCustomFields,
      base.favourite,
      base.shortcut,
      base.channels,
      base.subject,
      base.isEditable,
      base.category,
      base.mediaUrl,
      base.mediaType,
      base.buttons ?? []
    );
  }, [templates, templateId, selectedTemplateId, draftCustomFields]);
}

export function useReplySuggestion(
  conversationId: number | undefined,
  options?: { staleTimeMs?: number; enabled?: boolean }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<ReplySuggestionDomain | null, Error>({
    queryKey: replySuggestionQueryKey(merchant?.id, conversationId),
    queryFn: async ({ signal }) => {
      if (!merchant?.id || !conversationId) return null;

      try {
        return (
          (await ReplySuggestionService.getReplySuggestion(
            auth0Context,
            merchant.id,
            conversationId,
            signal
          )) || null
        );
      } catch (error) {
        return null;
      }
    },
    enabled:
      Boolean(merchant?.id && conversationId) && (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 30_000,
  });
}

export function useAiConversationHistoryBoundary(
  conversationId: number | undefined,
  options?: { staleTimeMs?: number; enabled?: boolean }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<
    { conversation_id: number; boundary_timestamp: string } | null,
    Error
  >({
    queryKey: aiBoundaryQueryKey(merchant?.id, conversationId),
    queryFn: async ({ signal }) => {
      if (!merchant?.id || !conversationId) return null;

      try {
        return (
          (await InboxService.getAiConversationHistoryBoundary(
            auth0Context,
            merchant.id,
            conversationId,
            signal
          )) || null
        );
      } catch (error) {
        return null;
      }
    },
    enabled:
      Boolean(merchant?.id && conversationId) && (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 60_000,
  });
}

export function useConversationByChannelId(
  channelId: string | undefined,
  options?: { staleTimeMs?: number; enabled?: boolean }
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<ConversationDomain | null, Error>({
    queryKey: conversationByChannelIdQueryKey(merchant?.id, channelId),
    queryFn: async ({ signal }) => {
      if (!merchant?.id || !channelId) return null;

      try {
        return await InboxService.getConversationByChannelId(
          auth0Context,
          channelId,
          merchant.id
        );
      } catch (error) {
        return null;
      }
    },
    enabled: Boolean(merchant?.id && channelId) && (options?.enabled ?? true),
    staleTime: options?.staleTimeMs ?? 60_000,
  });
}
